import useVisible from "../../hooks/useVisible.jsx";
import handleClick from "../../utils/handleClick.js";
import "../ResolutionTools/ResolutionTools.css";
import "./TextAnimator.css";

const BASE_ARGUMENTS = {
  duration: 2,
  easing: "cubic",
};

const BASED_ON_OPTIONS = [
  { label: "Characters", value: "characters" },
  { label: "Words", value: "words" },
  { label: "Lines", value: "lines" },
];

const DIRECTION_BUTTONS = [
  {
    label: "Left to Right",
    icon: "fa-solid fa-right",
    position: { x: -150, y: 0 },
  },
  {
    label: "Top to Bottom",
    icon: "fa-solid fa-down",
    position: { x: 0, y: -150 },
  },
  {
    label: "Right to Left",
    icon: "fa-solid fa-left",
    position: { x: 150, y: 0 },
  },
  {
    label: "Bottom to Top",
    icon: "fa-solid fa-up",
    position: { x: 0, y: 150 },
  },
];

const PROPERTY_OPTIONS = [
  {
    key: "position",
    label: "Position",
    icon: "fa-light fa-arrows-up-down-left-right",
    value: { x: 0, y: 0 },
  },
  {
    key: "scale",
    label: "Scale",
    icon: "fa-sharp fa-light fa-arrows-maximize",
    value: { x: 100, y: 100 },
  },
  {
    key: "rotation",
    label: "Rotation",
    icon: "fa-solid fa-rotate-right",
    value: 0,
  },
  {
    key: "opacity",
    label: "Opacity",
    icon: "fa-regular fa-circle-half-stroke",
    value: 0,
  },
  {
    key: "tracking",
    label: "Tracking",
    icon: "fa-light fa-text-width",
    value: 0,
  },
  {
    key: "blur",
    label: "Blur",
    icon: "fa-light fa-droplet",
    value: { x: 0, y: 0 },
  },
];

const propertyKeys = PROPERTY_OPTIONS.map((option) => option.key);

const cloneValue = (value) =>
  value && typeof value === "object" ? { ...value } : value;

function TextAnimator({ selectedTag, buttonTags, generateButtonTags }) {
  const [isVisible, setIsVisible] = useVisible("TextAnimatorGroupVisibility");
  const [basedOn, setBasedOn] = useVisible(
    "TextAnimatorBasedOn",
    "characters",
  );
  const [selectedProperties, setSelectedProperties] = useVisible(
    "TextAnimatorProperties",
    ["position"],
  );

  const activeBasedOn = BASED_ON_OPTIONS.some(
    (option) => option.value === basedOn,
  )
    ? basedOn
    : "characters";
  const activeProperties = Array.isArray(selectedProperties)
    ? selectedProperties.filter((key) => propertyKeys.includes(key))
    : [];

  const toggleProperty = (key) => {
    setSelectedProperties((currentProperties) => {
      const currentKeys = Array.isArray(currentProperties)
        ? currentProperties
        : [];

      if (currentKeys.includes(key)) {
        return currentKeys.filter((currentKey) => currentKey !== key);
      }

      return [...currentKeys, key];
    });
  };

  const handleCreate = () => {
    const propertiesPayload = PROPERTY_OPTIONS.reduce((result, option) => {
      if (!activeProperties.includes(option.key)) {
        return result;
      }

      return {
        ...result,
        [option.key]: cloneValue(option.value),
      };
    }, {});

    handleClick("textAnimator", {
      basedOn: activeBasedOn,
      ...BASE_ARGUMENTS,
      ...propertiesPayload,
    });

    return false;
  };

  if (selectedTag && !buttonTags.includes(selectedTag)) {
    return null;
  }

  return (
    <>
      <li
        onClick={() => setIsVisible(!isVisible)}
        className={`button-card resolution-toggle ${isVisible ? "active" : ""}`}
      >
        <div className="button-card__icon">
          <i className="fa-solid fa-text"></i>
          <div />
        </div>
        <div className="button-card__title">Text Animator</div>
        {generateButtonTags()}
      </li>
      <div
        className={`render-group text-animator ${
          isVisible ? "" : "render-group--hidden"
        }`}
      >
        <div className="text-animator__based-on" role="group">
          {BASED_ON_OPTIONS.map((option) => (
            <button
              type="button"
              key={option.value}
              onClick={() => setBasedOn(option.value)}
              className={`resolution-tools__button text-animator__toggle ${
                activeBasedOn === option.value ? "active" : ""
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className="resolution-tools text-animator__directions">
          {DIRECTION_BUTTONS.map((button) => (
            <div
              key={button.label}
              onClick={() =>
                handleClick("textAnimator", {
                  basedOn: activeBasedOn,
                  ...BASE_ARGUMENTS,
                  position: { ...button.position },
                  opacity: 0,
                })
              }
              className="resolution-tools__button"
            >
              <i className={button.icon}></i>
              <span>{button.label}</span>
            </div>
          ))}
        </div>

        <div className="text-animator__properties">
          {PROPERTY_OPTIONS.map((option) => {
            const isChecked = activeProperties.includes(option.key);

            return (
              <label
                key={option.key}
                className={`text-animator__property ${
                  isChecked ? "active" : ""
                }`}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => toggleProperty(option.key)}
                />
                <i className={option.icon}></i>
                <span>{option.label}</span>
              </label>
            );
          })}
        </div>

        <button
          type="button"
          onClick={handleCreate}
          className="resolution-tools__button text-animator__create"
        >
          <i className="fa-solid fa-wand-magic-sparkles"></i>
          <span>Create</span>
        </button>

        <div className="text-animator__actions">
          <button
            type="button"
            onClick={() => handleClick("updateTextAnimator")}
            className="resolution-tools__button text-animator__action-button"
          >
            <i className="fa-regular fa-rotate"></i>
            <span>Update</span>
          </button>
          <button
            type="button"
            onClick={() => handleClick("deleteTextAnimator")}
            className="resolution-tools__button text-animator__action-button"
          >
            <i className="fa-light fa-trash"></i>
            <span>Delete</span>
          </button>
        </div>
      </div>
    </>
  );
}

export default TextAnimator;
